import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { withLatestFrom, switchMap, catchError, of, map } from "rxjs";
import { BrandClient } from "src/api-service";
import { selectSelectedCompany } from "../../../user/user.selectors";
import { selectVendorState } from "../vendor.selectors";
import { VendorsActions } from "./vendors.actions";
import { normalizeError } from "../../../app-store-error.types";

// ============================================================================
// VENDORS LIST EFFECTS - Single effect, no chained actions
// ============================================================================

@Injectable()
export class VendorsEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly brandClient = inject(BrandClient);

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VendorsActions.load),
            withLatestFrom(
                this.store.select(selectSelectedCompany),
                this.store.select(selectVendorState),
            ),
            switchMap(([action, selectedCompany, state]) => {
                const companyId = selectedCompany?.id;
                if (!companyId) {
                    return of(VendorsActions.loadFailure({
                        error: { message: "No company selected" },
                        toastKey: "VENDOR_LIST.TOAST.LOAD_ERROR",
                    }));
                }

                // Merge: action params → current state → defaults
                const params = {
                    pagination: {
                        pageIndex: action.pagination?.pageIndex ?? state?.pageNumber ?? 1,
                        pageSize: action.pagination?.pageSize ?? state?.pageSize ?? 50,
                    },
                    sorting: {
                        sortProperty: action.sorting?.sortProperty ?? state?.sortProperty ?? "name",
                        sortAscending: action.sorting?.sortAscending ?? state?.sortAscending ?? true,
                    },
                    searchFilter: action.searchFilter ?? state?.searchFilter ?? "",
                };

                return this.brandClient
                    .getFiltered(
                        companyId,
                        params.pagination.pageIndex,
                        params.pagination.pageSize,
                        params.searchFilter,
                        params.sorting.sortProperty,
                        params.sorting.sortAscending,
                        null,
                    )
                    .pipe(
                        map(response => VendorsActions.loadSuccess({
                            vendors: response.data ?? [],
                            pagination: response.pagination,
                            params,
                        })),
                        catchError(error => of(VendorsActions.loadFailure({
                            error: normalizeError(error),
                            toastKey: "VENDOR_LIST.TOAST.LOAD_ERROR",
                        }))),
                    );
            }),
        ),
    );
}
